import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Center,
  Container,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import {
  publicBildirimOptionsSchema,
  publicBildirimSonucSchema,
  publicTakipDurumSchema,
} from '@isg/validation';
import type { z } from 'zod';
import { publicTakipDurumEtiketi } from '../i18n/tr';
import { ANLAM } from '../theme';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { useSahaGunu } from '../api/queries';
import { DemoSeridi } from '../shell/DemoSeridi';

/**
 * KAPI 2 — ANONİM BİLDİRİM PUBLIC YÜZEYİ (F3B·S4 — ✅ madde 64).
 *
 * OTURUM KATMANININ TAMAMEN DIŞINDA: bu dosya auth/token modüllerinden HİÇBİR
 * ŞEY import etmez — QR'ı okutan kişiye kimlik sorulmaz, kimlik çerezi/oturum
 * anahtarı bu rotaya sızmaz (e2e kilitler). Tek sayfa: bildirim formu + takip
 * numarası sorgusu (aynı yüzeyden — madde 64).
 *
 * Sunucu kuralları (ekran taşıyıcı): tür yalnız tehlikeli durum/davranış
 * (U1 — options zaten süzülmüş gelir) · işyeri/lokasyon TOKENDAN (gövdede
 * yok) · anahtar kapalıysa 404 "kapalı" · hız sınırı 429 · bozuk token 401.
 */

type PublicOptions = z.output<typeof publicBildirimOptionsSchema>;
type TakipDurum = z.output<typeof publicTakipDurumSchema>;

class PublicApiHata extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function publicFetch<T>(
  path: string,
  schema: { parse(value: unknown): T },
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(path, init);
  if (!response.ok) {
    const govde = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new PublicApiHata(
      response.status,
      govde?.message ?? `İstek başarısız (${response.status})`,
    );
  }
  return schema.parse(await response.json());
}

export function PublicBildirimScreen(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const options = useQuery<PublicOptions, PublicApiHata>({
    queryKey: ['public-bildirim', token],
    queryFn: () =>
      publicFetch(
        `/api/public/bildirim/options?t=${encodeURIComponent(token)}`,
        publicBildirimOptionsSchema,
      ),
    retry: false,
  });

  return (
    <>
      {/* Demo şeridi oturumsuz yüzeyin KÖKÜNDE (25.09.2026 — shell/DemoSeridi.tsx);
          demo kipi kapalıyken null. Kimlik/oturum kodu bu dala yine girmez. */}
      <DemoSeridi />
      <Container size="xs" py="xl">
        <Stack data-testid="public-bildirim-sayfasi">
          <Title order={2}>Güvenlik bildirimi</Title>
          <Text size="sm" c="dimmed">
            Bu sayfadan yaptığınız bildirim <strong>anonimdir</strong> — kimlik bilgisi istenmez ve
            kaydedilmez. Bildiriminize bir takip numarası verilir; durumunu aşağıdaki sorgudan
            izleyebilirsiniz.
          </Text>
          {options.isPending && (
            <Center>
              <Text size="sm">Yükleniyor…</Text>
            </Center>
          )}
          {options.isError && (
            <Alert
              color={options.error.status === 404 ? ANLAM.dikkat : ANLAM.engel}
              role="alert"
              data-testid="public-bildirim-hata"
            >
              {options.error.status === 404
                ? 'Anonim bildirim kanalı şu anda kapalı.'
                : options.error.status === 401
                  ? 'Bildirim bağlantısı geçersiz — lütfen asılı QR kodunu yeniden okutun.'
                  : options.error.message}
            </Alert>
          )}
          {options.isSuccess && <BildirimFormu token={token} options={options.data} />}
          <TakipSorgusu token={token} />
        </Stack>
      </Container>
    </>
  );
}

function BildirimFormu({
  token,
  options,
}: {
  token: string;
  options: PublicOptions;
}): React.JSX.Element {
  const [turId, setTurId] = useState<string | null>(null);
  const [tarih, setTarih] = useState('');
  // Madde 302: takvimin üst sınırı SAHA GÜNÜ — uç @Public, oturumsuz sayfa da okur.
  const sahaGunu = useSahaGunu();
  const [anlati, setAnlati] = useState('');
  const [takipNo, setTakipNo] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  if (takipNo) {
    return (
      <Card withBorder>
        <Stack gap="xs">
          <Alert color={ANLAM.olumlu} data-testid="public-takip-no">
            Bildiriminiz alındı. Takip numaranız: <strong>{takipNo}</strong> — bu numarayı saklayın;
            durumu aşağıdaki sorgudan izleyebilirsiniz.
          </Alert>
        </Stack>
      </Card>
    );
  }

  // §2.40 — sessiz düğmenin QR/anonim kanaldaki ikizi (aynı hata, aynı
  // çözüm: geçersiz tarihte düğme KAPALI ve sebebi alanın altında yazar).
  const tarihIso = gunGirdisiIso(tarih);
  const tarihHatasi = tarih.trim() !== '' && tarihIso === null;
  const hazir = turId !== null && tarihIso !== null && anlati.trim() !== '';
  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text fw={600} data-testid="public-bildirim-yer">
          {options.workplaceAd}
          {options.locationAd ? ` — ${options.locationAd}` : ''}
        </Text>
        <Select
          label="Ne gördünüz?"
          required
          data-testid="public-tur"
          data={options.turler.map((t) => ({ value: t.id, label: t.ad }))}
          value={turId}
          onChange={setTurId}
        />
        <TextInput
          type="date"
          label="Tarih"
          required
          max={sahaGunu.data}
          data-testid="public-tarih"
          value={tarih}
          error={tarihHatasi ? 'Geçerli bir tarih seçin (gün.ay.yıl).' : null}
          onChange={(event) => setTarih(event.currentTarget.value)}
        />
        <Textarea
          label="Anlatın"
          required
          minRows={4}
          data-testid="public-anlati"
          value={anlati}
          onChange={(event) => setAnlati(event.currentTarget.value)}
        />
        {hata && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="public-gonder-hata">
            {hata}
          </Text>
        )}
        <Group>
          <Button
            data-testid="public-gonder"
            disabled={!hazir}
            loading={gonderiliyor}
            onClick={() => {
              if (!hazir || !turId || tarihIso === null) return;
              setGonderiliyor(true);
              setHata(null);
              publicFetch('/api/public/bildirim', publicBildirimSonucSchema, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  token,
                  olayTuruRefId: turId,
                  olayTarihi: tarihIso,
                  anlati: anlati.trim(),
                }),
              })
                .then((sonuc) => setTakipNo(sonuc.takipNo))
                .catch((err: unknown) =>
                  setHata(err instanceof Error ? err.message : 'Gönderim başarısız'),
                )
                .finally(() => setGonderiliyor(false));
            }}
          >
            Anonim bildir
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function TakipSorgusu({ token }: { token: string }): React.JSX.Element {
  const [no, setNo] = useState('');
  const [sonuc, setSonuc] = useState<TakipDurum | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text fw={600}>Takip numarası sorgusu</Text>
        <Group align="end">
          <TextInput
            label="Takip numaranız"
            placeholder="OLY-XXXXXXXX"
            data-testid="public-takip-girdi"
            value={no}
            onChange={(event) => setNo(event.currentTarget.value)}
          />
          <Button
            variant="default"
            data-testid="public-takip-sorgula"
            disabled={no.trim() === ''}
            onClick={() => {
              setHata(null);
              setSonuc(null);
              publicFetch(
                `/api/public/bildirim/takip/${encodeURIComponent(no.trim())}?t=${encodeURIComponent(token)}`,
                publicTakipDurumSchema,
              )
                .then(setSonuc)
                .catch((err: unknown) =>
                  setHata(err instanceof Error ? err.message : 'Sorgu başarısız'),
                );
            }}
          >
            Sorgula
          </Button>
        </Group>
        {hata && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="public-takip-hata">
            {hata}
          </Text>
        )}
        {sonuc && (
          <Text size="sm" data-testid="public-takip-sonuc">
            {/* D2: yanıt yalnız durum + tarih taşır — içerik sızmaz. */}
            Durum: <strong>{publicTakipDurumEtiketi(sonuc.durum, sonuc.kapali)}</strong> · Olay
            tarihi: {tarihGoster(sonuc.olayTarihi)}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
